/**
 * AIAssistantPanel — panel Trợ lý AI thường trực bên phải workspace.
 *
 * Context-aware: hiển thị lời chào, gợi ý và hành động nhanh theo ngữ cảnh
 * trang hiện tại (trang gọi `setAIContext(...)`). Có khung chat thực sự,
 * tái dùng API `generateStudyAssistant` qua WorkspaceProvider.
 *
 * 2 chế độ hiển thị:
 *  - Có hội thoại (messages.length > 0): hiện khung chat cuộn.
 *  - Chưa có (trống): hiện lời chào + gợi ý + hành động nhanh.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, X, Send, RotateCcw, Brain, Trash2, Loader2, Plus } from 'lucide-react';
import { useWorkspace } from '../context/WorkspaceProvider';
import {
  addAssistantMemoryFact,
  clearAssistantMemory,
  deleteAssistantMemoryFact,
  getAssistantMemory,
  getEduappAuthType,
  type AssistantMemoryData,
} from '../../../services/apiService';

interface AIAssistantPanelProps {
  userName?: string;
}

const DOT_DEFAULT = 'bg-indigo-500';

/** Render **đậm** trong 1 dòng văn bản (không dùng HTML thô — an toàn XSS). */
const renderInline = (text: string): React.ReactNode[] =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i} className="font-bold text-slate-800">
        {part.slice(2, -2)}
      </strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );

/**
 * Markdown-lite cho tin nhắn AI: tiêu đề (#), gạch đầu dòng,
 * danh sách đánh số và chữ đậm. Đủ cho phản hồi giáo án/tư vấn dài mà
 * không cần thư viện markdown.
 */
const AiMessageText: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split('\n').map((line, i) => {
      const heading = line.match(/^#{1,4}\s+(.*)/);
      if (heading) {
        return (
          <div key={i} className="font-bold text-slate-800 mt-1.5 first:mt-0">
            {renderInline(heading[1])}
          </div>
        );
      }
      const bullet = line.match(/^\s*[-*•]\s+(.*)/);
      if (bullet) {
        return (
          <div key={i} className="flex gap-1.5 mt-0.5">
            <span className="text-indigo-400 shrink-0">•</span>
            <span>{renderInline(bullet[1])}</span>
          </div>
        );
      }
      const numbered = line.match(/^\s*(\d+)[.)]\s+(.*)/);
      if (numbered) {
        return (
          <div key={i} className="flex gap-1.5 mt-0.5">
            <span className="text-indigo-500 font-semibold shrink-0">{numbered[1]}.</span>
            <span>{renderInline(numbered[2])}</span>
          </div>
        );
      }
      if (line.trim() === '') return <div key={i} className="h-2" />;
      return <div key={i}>{renderInline(line)}</div>;
    })}
  </>
);

/**
 * Gợi ý mẫu ghi nhớ theo vai trò — bấm chip để điền sẵn vào ô nhập,
 * người dùng chỉnh phần "..." rồi lưu. Giúp hiểu ngay nên ghi nhớ gì.
 */
const MEMORY_TEMPLATES: Record<'student' | 'teacher' | 'parent', string[]> = {
  student: [
    'Mục tiêu của mình là thi vào lớp 10 trường ...',
    'Mình muốn cải thiện môn ...',
    'Mình học dễ hiểu nhất khi có ví dụ thực tế',
    'Mình chuẩn bị thi ... vào tháng ...',
  ],
  teacher: [
    'Tôi đang dạy môn ... khối ...',
    'Tôi thích giáo án chi tiết, chia hoạt động rõ ràng',
    'Lớp tôi có nhiều học sinh cần hỗ trợ thêm môn ...',
    'Khi ra đề tôi luôn cần câu hỏi phân hóa 3 mức độ',
  ],
  parent: [
    'Con tôi đang học lớp ...',
    'Con tôi gặp khó khăn với môn ...',
    'Tôi muốn đồng hành cùng con nhẹ nhàng, không tạo áp lực',
    'Gia đình đặt mục tiêu cho con thi vào ...',
  ],
};

/**
 * MemoryView — "Trợ lý biết gì về bạn": hiển thị hồ sơ tổng hợp + ghi nhớ
 * dài hạn (minh bạch), cho phép TỰ THÊM (chip gợi ý theo vai trò) và xóa
 * từng mục hoặc toàn bộ. Fact 'user' có badge riêng, được ưu tiên giữ.
 */
const MemoryView: React.FC<{ mode: 'student' | 'teacher' | 'parent'; onClose: () => void }> = ({
  mode,
  onClose,
}) => {
  const [data, setData] = useState<AssistantMemoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newFact, setNewFact] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const addInputRef = useRef<HTMLInputElement>(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError('');
    getAssistantMemory(mode)
      .then(setData)
      .catch((e) => setError(e?.message || 'Không thể tải bộ nhớ.'))
      .finally(() => setLoading(false));
  }, [mode]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    try {
      await deleteAssistantMemoryFact(id);
      setData((d) => (d ? { ...d, facts: d.facts.filter((f) => f.id !== id) } : d));
    } catch { /* giữ nguyên nếu lỗi */ }
  };

  const handleClearAll = async () => {
    try {
      await clearAssistantMemory(mode);
      setData((d) => (d ? { ...d, facts: [] } : d));
    } catch { /* noop */ }
  };

  const handleAdd = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const content = newFact.trim();
    if (content.length < 8 || adding) {
      if (content.length > 0 && content.length < 8) setAddError('Ghi nhớ quá ngắn — hãy viết thành 1 câu đầy đủ.');
      return;
    }
    setAdding(true);
    setAddError('');
    try {
      const fact = await addAssistantMemoryFact(content, mode);
      setNewFact('');
      if (fact) {
        setData((d) => (d ? { ...d, facts: [fact, ...d.facts.filter((f) => f.id !== fact.id)] } : d));
      } else {
        load();
      }
    } catch (err: any) {
      setAddError(err?.message || 'Không thể thêm ghi nhớ.');
    } finally {
      setAdding(false);
    }
  };

  /** Chip gợi ý: điền sẵn vào ô nhập + focus để người dùng chỉnh phần "..." */
  const applyTemplate = (tpl: string) => {
    setNewFact(tpl);
    setAddError('');
    addInputRef.current?.focus();
  };

  // Phần hồ sơ phái sinh (trước dòng "Ghi nhớ từ...") — bỏ dòng tiêu đề kỹ thuật.
  const profileLines = (data?.profile || '')
    .split('\n')
    .filter((l) => l.trim() !== '' && !l.startsWith('HỒ SƠ NGƯỜI DÙNG') && !l.startsWith('Ghi nhớ từ') && !l.startsWith('- '));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
          Trợ lý biết gì về bạn
        </span>
        <button onClick={onClose} className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800">
          ← Quay lại
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-400 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Đang tải...
        </div>
      ) : error ? (
        <p className="text-xs text-rose-500">{error}</p>
      ) : !data?.enabled ? (
        <p className="text-xs text-slate-500">Cá nhân hóa đang tắt trên hệ thống.</p>
      ) : (
        <>
          {/* Tự thêm ghi nhớ — chip mẫu theo vai trò để biết ngay nên viết gì */}
          <div className="bg-indigo-50/40 border border-indigo-100 rounded-xl p-3">
            <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider block mb-2">
              ✍️ Cho trợ lý biết về bạn
            </span>
            <form onSubmit={handleAdd} className="relative mb-2">
              <input
                ref={addInputRef}
                value={newFact}
                onChange={(e) => { setNewFact(e.target.value); setAddError(''); }}
                type="text"
                maxLength={400}
                placeholder={MEMORY_TEMPLATES[mode][0]}
                disabled={adding}
                className="w-full bg-white text-slate-800 placeholder-slate-400 pr-9 pl-3 py-2 rounded-lg border border-indigo-100 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-xs outline-none disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={adding || newFact.trim().length < 8}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white rounded-md flex items-center justify-center transition-all"
                title="Lưu ghi nhớ"
              >
                {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              </button>
            </form>
            {addError && <p className="text-[10px] text-rose-500 mb-1.5">{addError}</p>}
            <div className="flex flex-wrap gap-1.5">
              {MEMORY_TEMPLATES[mode].map((tpl) => (
                <button
                  key={tpl}
                  onClick={() => applyTemplate(tpl)}
                  className="text-[10px] font-medium text-indigo-600 bg-white border border-indigo-100 hover:border-indigo-300 rounded-full px-2.5 py-1 transition-colors text-left"
                >
                  {tpl.length > 42 ? tpl.slice(0, 42) + '…' : tpl}
                </button>
              ))}
            </div>
          </div>

          {profileLines.length > 0 && (
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                Hồ sơ học tập (tự tổng hợp từ dữ liệu)
              </span>
              <div className="space-y-1">
                {profileLines.map((l, i) => (
                  <p key={i} className="text-xs text-slate-600 leading-relaxed">{l}</p>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Ghi nhớ từ hội thoại ({data.facts.length})
              </span>
              {data.facts.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="text-[10px] font-bold text-rose-500 hover:text-rose-700"
                >
                  Xóa tất cả
                </button>
              )}
            </div>
            {data.facts.length === 0 ? (
              <p className="text-xs text-slate-400">
                Chưa có ghi nhớ nào. Hãy tự thêm ở ô phía trên — hoặc trợ lý sẽ tự ghi nhớ mục tiêu,
                sở thích học tập... qua các cuộc trò chuyện.
              </p>
            ) : (
              <div className="space-y-1.5">
                {data.facts.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-start gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-600 leading-relaxed">{f.content}</p>
                      <span
                        className={`inline-block mt-1 text-[9px] font-bold uppercase tracking-wider ${
                          f.source === 'user' ? 'text-indigo-400' : 'text-slate-300'
                        }`}
                      >
                        {f.source === 'user' ? '✍️ Bạn thêm' : '🤖 AI ghi nhớ'}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDelete(f.id)}
                      className="p-0.5 text-slate-300 hover:text-rose-500 transition-colors shrink-0"
                      title="Xóa ghi nhớ này"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="text-[10px] text-slate-400 leading-relaxed">
            Trợ lý dùng các thông tin này để trả lời sát với bạn hơn. Bạn toàn quyền xóa bất kỳ mục nào.
          </p>
        </>
      )}
    </div>
  );
};

export const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({ userName }) => {
  const {
    mode,
    isAIPanelOpen,
    closeAIPanel,
    aiContext,
    messages,
    isAILoading,
    sendMessage,
    resetChat,
  } = useWorkspace();

  const [input, setInput] = useState('');
  const [showMemory, setShowMemory] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Bộ nhớ cá nhân hóa chỉ cho tài khoản Passport (endpoint auth:api).
  const canViewMemory = getEduappAuthType() !== 'student_code';

  // Cuộn xuống cuối khi có tin mới.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  if (!isAIPanelOpen) return null;

  const greeting = (aiContext.greeting || 'Xin chào{name}! 👋 Tôi có thể giúp gì cho bạn?').replace(
    '{name}',
    userName ? ` ${userName}` : '',
  );
  const hasChat = messages.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput('');
    void sendMessage(text);
  };

  const handleSuggestion = (prompt: string) => {
    void sendMessage(prompt);
  };

  return (
    <aside className="w-[340px] bg-white border-l border-slate-200 flex flex-col shrink-0 z-30 h-full">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600">
            <Sparkles className="w-5 h-5" strokeWidth={2.2} />
          </div>
          <h2 className="font-bold text-slate-800 text-sm">Trợ lý AI</h2>
        </div>
        <div className="flex items-center gap-1">
          {canViewMemory && (
            <button
              onClick={() => setShowMemory((v) => !v)}
              className={`p-1.5 hover:bg-slate-100 rounded transition-colors ${
                showMemory ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:text-slate-600'
              }`}
              title="Trợ lý biết gì về bạn"
            >
              <Brain className="w-4 h-4" />
            </button>
          )}
          {hasChat && (
            <button
              onClick={resetChat}
              className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-colors"
              title="Hội thoại mới"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={closeAIPanel}
            className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-colors"
            title="Đóng"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-5">
        {showMemory ? (
          <MemoryView mode={mode} onClose={() => setShowMemory(false)} />
        ) : !hasChat ? (
          <>
            {/* Lời chào */}
            <div className="bg-indigo-50/50 rounded-2xl p-4 border border-indigo-50">
              <p className="text-xs text-slate-600 leading-relaxed font-medium">{greeting}</p>
              {canViewMemory && (
                <button
                  onClick={() => setShowMemory(true)}
                  className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                  <Brain className="w-3.5 h-3.5" />
                  Cho trợ lý biết về bạn để được hỗ trợ sát hơn →
                </button>
              )}
            </div>

            {/* Gợi ý */}
            {aiContext.suggestions && aiContext.suggestions.length > 0 && (
              <div>
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block mb-3">
                  {aiContext.suggestionsLabel || 'Gợi ý cho bạn'}
                </span>
                <div className="space-y-2.5">
                  {aiContext.suggestions.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => handleSuggestion(s.prompt)}
                      className="w-full text-left bg-white hover:bg-indigo-50/20 border border-slate-200 hover:border-indigo-200 p-3 rounded-xl flex items-center justify-between gap-3 group transition-all"
                    >
                      <span className="text-xs font-semibold text-slate-700 group-hover:text-indigo-700">
                        {s.label}
                      </span>
                      <span className="text-slate-400 group-hover:text-indigo-600 text-lg leading-none">
                        +
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Hành động nhanh */}
            {aiContext.quickActions && aiContext.quickActions.length > 0 && (
              <div>
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block mb-3">
                  Hành động nhanh
                </span>
                <div className="space-y-1.5">
                  {aiContext.quickActions.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => handleSuggestion(a.prompt)}
                      className="w-full text-left py-2 px-3 rounded-lg hover:bg-slate-50 font-medium text-slate-600 flex items-center gap-2 transition-colors text-xs"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${a.dotColor || DOT_DEFAULT}`} />
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          /* Khung chat */
          <div className="space-y-4">
            {messages.map((m) =>
              m.role === 'user' ? (
                <div key={m.id} className="flex justify-end">
                  <div className="max-w-[85%] bg-indigo-600 text-white text-xs leading-relaxed px-3.5 py-2.5 rounded-2xl rounded-br-sm">
                    {m.text}
                  </div>
                </div>
              ) : (
                <div key={m.id} className="flex justify-start">
                  <div className="max-w-[88%] bg-slate-50 border border-slate-100 text-slate-700 text-xs leading-relaxed px-3.5 py-2.5 rounded-2xl rounded-bl-sm">
                    {m.pending ? (
                      <span className="inline-flex gap-1 items-center text-slate-400">
                        <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-slate-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                    ) : (
                      <AiMessageText text={m.text} />
                    )}
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-slate-200">
        <form onSubmit={handleSubmit} className="relative">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            type="text"
            placeholder="Nhập yêu cầu của bạn..."
            disabled={isAILoading}
            className="w-full bg-slate-100 focus:bg-white text-slate-800 placeholder-slate-400 pr-10 pl-4 py-2.5 rounded-xl border border-transparent focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-xs transition-all outline-none disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={isAILoading || !input.trim()}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white rounded-lg flex items-center justify-center transition-all shadow-sm"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </form>
        <div className="text-[9px] text-slate-400 mt-2 text-center select-none">
          AI có thể mắc lỗi. Hãy kiểm tra lại thông tin.
        </div>
      </div>
    </aside>
  );
};
